/**
 * Acquisition Attribution Persistence (PBI #1104, last-touch #1204, AI referrer GEO, OpenAI #1263, cross-visit #1500)
 *
 * Captures first-touch ad click IDs and UTMs from the landing URL into
 * sessionStorage so they survive the same-tab MSAL redirect to Entra and
 * back. After auth, POST /api/account/acquisition persists them on User
 * (null-only). Cleared after successful persist — not whitelisted across logout
 * (would leak across account switches).
 *
 * When the URL has no click ids or UTMs, known AI assistant referrers
 * (ChatGPT, Perplexity, etc.) are captured as utmSource + utmMedium=ai_assistant
 * so signups from AI search are distinguishable without a DB migration.
 *
 * ChatGPT Ads clicks append `oppref` — captured like other click ids for Admin
 * reconciliation (separate from Measurement Pixel consent gating).
 *
 * Last-touch Google click ids (gclid / gbraid / wbraid) are stored separately
 * with overwrite semantics for server-side conversion upload (PBI #1204).
 *
 * PBI #1500: Marketing-consented first-touch may persist in localStorage for
 * 30 days; signup UTM snapshot survives post-flush until free_signup fires.
 */

import {
  AI_ASSISTANT_UTM_MEDIUM,
  normalizeAiAssistantReferrerHost,
} from '../constants/aiAssistantReferrers';
import { CONSENT_STORAGE_KEY, CURRENT_CONSENT_VERSION } from '../constants/consent';
import type { ConsentState, StoredConsent } from '../types/consent';
import { detectGpc } from '../utils/detectGpc';

/** sessionStorage key for the JSON first-touch attribution payload. */
export const ACQUISITION_ATTRIBUTION_STORAGE_KEY =
  'scheduledsmiles_acquisition_attribution';

/** sessionStorage key for last-touch Google click ids (overwrite on each capture). */
export const ACQUISITION_ATTRIBUTION_LAST_TOUCH_STORAGE_KEY =
  'scheduledsmiles_acquisition_attribution_last_touch';

/** localStorage key for Marketing-gated cross-visit first-touch (PBI #1500). */
export const ACQUISITION_ATTRIBUTION_DURABLE_STORAGE_KEY =
  'scheduledsmiles_acquisition_attribution_durable';

/** sessionStorage UTM snapshot for delayed free_signup after acquisition POST (PBI #1500). */
export const ACQUISITION_SIGNUP_UTM_SNAPSHOT_KEY =
  'scheduledsmiles_acquisition_signup_utm_snapshot';

/** Fixed retention from first touch — never extended on later visits. */
export const ACQUISITION_DURABLE_RETENTION_DAYS = 30;

/** Paid-style utm_medium values used for client-side CameViaPaidAd hints. */
export const PAID_UTM_MEDIUMS = [
  'cpc',
  'ppc',
  'paid',
  'paidsearch',
  'paid_social',
  'paidsocial',
] as const;

/**
 * First-touch attribution captured from the landing URL.
 */
export interface AcquisitionAttribution {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  msclkid?: string;
  fbclid?: string;
  /** ChatGPT Ads click identifier (query param `oppref`). */
  oppref?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  landingPath?: string;
}

export interface AcquisitionFirstTouchEnvelope {
  capturedAtIso: string;
  attribution: AcquisitionAttribution;
}

export interface AcquisitionDurableEnvelope extends AcquisitionFirstTouchEnvelope {
  expiresAtIso: string;
}

export interface SignupUtmSnapshot {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/** All first-touch network click id query params. */
const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'oppref'] as const;

/** Google click ids that participate in last-touch conversion upload (#1204). */
const GOOGLE_LAST_TOUCH_CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid'] as const;

const UTM_PARAMS: ReadonlyArray<{ query: string; field: keyof AcquisitionAttribution }> = [
  { query: 'utm_source', field: 'utmSource' },
  { query: 'utm_medium', field: 'utmMedium' },
  { query: 'utm_campaign', field: 'utmCampaign' },
  { query: 'utm_content', field: 'utmContent' },
  { query: 'utm_term', field: 'utmTerm' },
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const parseAttributionFromUnknown = (value: unknown): AcquisitionAttribution | null => {
  if (!isRecord(value)) {
    return null;
  }
  return value as AcquisitionAttribution;
};

/**
 * Parses session storage JSON — flat legacy shape or `{ capturedAtIso, attribution }`.
 */
const parseSessionFirstTouchRaw = (raw: string): AcquisitionFirstTouchEnvelope | null => {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) {
      return null;
    }
    if (isRecord(parsed.attribution) && typeof parsed.capturedAtIso === 'string') {
      const attribution = parseAttributionFromUnknown(parsed.attribution);
      if (!attribution) {
        return null;
      }
      return { capturedAtIso: parsed.capturedAtIso, attribution };
    }
    const flat = parseAttributionFromUnknown(parsed);
    if (!flat) {
      return null;
    }
    return { capturedAtIso: new Date().toISOString(), attribution: flat };
  } catch {
    return null;
  }
};

const readSessionFirstTouchEnvelope = (): AcquisitionFirstTouchEnvelope | null => {
  try {
    const raw = sessionStorage.getItem(ACQUISITION_ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return parseSessionFirstTouchRaw(raw);
  } catch {
    return null;
  }
};

const writeSessionFirstTouchEnvelope = (envelope: AcquisitionFirstTouchEnvelope): void => {
  try {
    sessionStorage.setItem(ACQUISITION_ATTRIBUTION_STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    console.warn('Failed to save acquisition attribution to sessionStorage');
  }
};

/** Explicit overwrite path for restore / merge — not first-touch save semantics. */
export const applyRestoredFirstTouch = (envelope: AcquisitionFirstTouchEnvelope): void => {
  writeSessionFirstTouchEnvelope(envelope);
};

export const mergeFirstTouchEnvelopes = (
  a: AcquisitionFirstTouchEnvelope,
  b: AcquisitionFirstTouchEnvelope,
): AcquisitionFirstTouchEnvelope => {
  const aTime = Date.parse(a.capturedAtIso);
  const bTime = Date.parse(b.capturedAtIso);
  if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime <= bTime) {
    return a;
  }
  if (!Number.isNaN(bTime) && (Number.isNaN(aTime) || bTime < aTime)) {
    return b;
  }
  return a;
};

const addRetentionDays = (capturedAtIso: string): string => {
  const capturedMs = Date.parse(capturedAtIso);
  const base = Number.isNaN(capturedMs) ? Date.now() : capturedMs;
  return new Date(base + ACQUISITION_DURABLE_RETENTION_DAYS * MS_PER_DAY).toISOString();
};

const readDurableEnvelope = (): AcquisitionDurableEnvelope | null => {
  try {
    const raw = localStorage.getItem(ACQUISITION_ATTRIBUTION_DURABLE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (
      !isRecord(parsed)
      || typeof parsed.capturedAtIso !== 'string'
      || typeof parsed.expiresAtIso !== 'string'
      || !isRecord(parsed.attribution)
    ) {
      deleteDurableAcquisition();
      return null;
    }
    const expiresMs = Date.parse(parsed.expiresAtIso);
    if (Number.isNaN(expiresMs) || expiresMs <= Date.now()) {
      deleteDurableAcquisition();
      return null;
    }
    const attribution = parseAttributionFromUnknown(parsed.attribution);
    if (!attribution) {
      deleteDurableAcquisition();
      return null;
    }
    return {
      capturedAtIso: parsed.capturedAtIso,
      expiresAtIso: parsed.expiresAtIso,
      attribution,
    };
  } catch {
    deleteDurableAcquisition();
    return null;
  }
};

const writeDurableFromSessionEnvelope = (session: AcquisitionFirstTouchEnvelope): void => {
  if (!readMarketingConsentForAcquisition()) {
    return;
  }
  if (readDurableEnvelope()) {
    return;
  }
  try {
    const durable: AcquisitionDurableEnvelope = {
      capturedAtIso: session.capturedAtIso,
      expiresAtIso: addRetentionDays(session.capturedAtIso),
      attribution: session.attribution,
    };
    localStorage.setItem(ACQUISITION_ATTRIBUTION_DURABLE_STORAGE_KEY, JSON.stringify(durable));
  } catch {
    console.warn('Failed to save durable acquisition attribution');
  }
};

export const deleteDurableAcquisition = (): void => {
  try {
    localStorage.removeItem(ACQUISITION_ATTRIBUTION_DURABLE_STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
};

/**
 * Marketing consent + current CMP version + no GPC — required for durable tier I/O.
 */
export const readMarketingConsentForAcquisition = (): boolean => {
  if (detectGpc()) {
    return false;
  }
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.version !== CURRENT_CONSENT_VERSION) {
      return false;
    }
    return parsed.consent?.marketing === true;
  } catch {
    return false;
  }
};

/**
 * Boot-only: restore / merge durable first-touch into session when Marketing allows.
 */
export const restoreAcquisitionAttributionFromDurableIfNeeded = (): void => {
  if (!readMarketingConsentForAcquisition()) {
    return;
  }

  const durable = readDurableEnvelope();
  if (!durable) {
    return;
  }

  const session = readSessionFirstTouchEnvelope();
  if (!session) {
    applyRestoredFirstTouch({
      capturedAtIso: durable.capturedAtIso,
      attribution: durable.attribution,
    });
    return;
  }

  const winner = mergeFirstTouchEnvelopes(session, durable);
  applyRestoredFirstTouch(winner);
};

/**
 * Keeps durable tier aligned with CMP marketing choice (App.tsx consent effect).
 */
export const syncAcquisitionAttributionWithConsent = (
  consent: ConsentState,
  isConsentSet: boolean,
): void => {
  if (!isConsentSet) {
    deleteDurableAcquisition();
    return;
  }

  const marketingAllowed = consent.marketing && !detectGpc();
  if (!marketingAllowed) {
    deleteDurableAcquisition();
    return;
  }

  const session = readSessionFirstTouchEnvelope();
  if (session) {
    writeDurableFromSessionEnvelope(session);
  }
};

/**
 * Reads attribution query params from a URLSearchParams + pathname.
 * Returns null when nothing attribution-related is present.
 */
export const parseAcquisitionAttributionFromUrl = (
  search: string | URLSearchParams,
  pathname: string
): AcquisitionAttribution | null => {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const result: AcquisitionAttribution = {};
  let hasAny = false;

  for (const key of CLICK_ID_PARAMS) {
    const value = params.get(key)?.trim();
    if (value) {
      result[key] = value;
      hasAny = true;
    }
  }

  for (const { query, field } of UTM_PARAMS) {
    const value = params.get(query)?.trim();
    if (value) {
      result[field] = value;
      hasAny = true;
    }
  }

  const path = pathname?.trim();
  if (path) {
    result.landingPath = path;
  }

  // Landing path alone is not enough — only persist when a click id or UTM is present.
  if (!hasAny) {
    return null;
  }

  return result;
};

/**
 * Builds first-touch attribution from document.referrer when the visitor arrived
 * from a known AI assistant and the URL carried no click ids or UTMs.
 */
export const parseAiAssistantReferrerAttribution = (
  referrer: string,
  pathname: string,
): AcquisitionAttribution | null => {
  const trimmedReferrer = referrer.trim();
  if (!trimmedReferrer) {
    return null;
  }

  try {
    const host = normalizeAiAssistantReferrerHost(new URL(trimmedReferrer).hostname);
    if (!host) {
      return null;
    }

    const path = pathname?.trim();
    return {
      utmSource: host,
      utmMedium: AI_ASSISTANT_UTM_MEDIUM,
      ...(path ? { landingPath: path } : {}),
    };
  } catch {
    return null;
  }
};

/**
 * True when the parsed payload includes at least one first-touch click id.
 */
const hasClickIdAttribution = (attribution: AcquisitionAttribution): boolean =>
  CLICK_ID_PARAMS.some((key) => Boolean(attribution[key]?.trim()));

/**
 * True when a Google click id is present for last-touch conversion upload.
 */
const hasGoogleLastTouchClickId = (attribution: AcquisitionAttribution): boolean =>
  GOOGLE_LAST_TOUCH_CLICK_ID_PARAMS.some((key) => Boolean(attribution[key]?.trim()));

/**
 * Saves attribution with first-touch semantics (does not overwrite an existing payload).
 */
export const saveAcquisitionAttributionFirstTouch = (
  attribution: AcquisitionAttribution
): void => {
  try {
    if (sessionStorage.getItem(ACQUISITION_ATTRIBUTION_STORAGE_KEY)) {
      return;
    }
    writeSessionFirstTouchEnvelope({
      capturedAtIso: new Date().toISOString(),
      attribution,
    });
    const session = readSessionFirstTouchEnvelope();
    if (session) {
      writeDurableFromSessionEnvelope(session);
    }
  } catch {
    console.warn('Failed to save acquisition attribution');
  }
};

/**
 * Saves last-touch Google click ids with overwrite semantics (PBI #1204).
 */
export const saveAcquisitionAttributionLastTouch = (
  attribution: AcquisitionAttribution
): void => {
  if (!hasGoogleLastTouchClickId(attribution)) {
    return;
  }

  const lastTouch: AcquisitionAttribution = {};
  for (const key of GOOGLE_LAST_TOUCH_CLICK_ID_PARAMS) {
    const value = attribution[key]?.trim();
    if (value) {
      lastTouch[key] = value;
    }
  }

  try {
    sessionStorage.setItem(
      ACQUISITION_ATTRIBUTION_LAST_TOUCH_STORAGE_KEY,
      JSON.stringify(lastTouch)
    );
  } catch {
    console.warn('Failed to save last-touch acquisition attribution');
  }
};

/**
 * Loads persisted last-touch click ids, or null if none / unavailable.
 */
export const loadAcquisitionAttributionLastTouch = (): AcquisitionAttribution | null => {
  try {
    const raw = sessionStorage.getItem(ACQUISITION_ATTRIBUTION_LAST_TOUCH_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as AcquisitionAttribution;
  } catch {
    return null;
  }
};

/**
 * Captures attribution from the current window location when present.
 * First-touch is preserved; last-touch Google click ids overwrite on each hit.
 * Safe to call on every boot / navigation.
 */
export const captureAcquisitionAttributionFromLocation = (
  location: Pick<Location, 'search' | 'pathname'> = window.location,
  referrer: string = typeof document !== 'undefined' ? document.referrer : '',
): void => {
  const parsed = parseAcquisitionAttributionFromUrl(location.search, location.pathname);
  if (parsed) {
    saveAcquisitionAttributionFirstTouch(parsed);
    if (hasClickIdAttribution(parsed)) {
      saveAcquisitionAttributionLastTouch(parsed);
    }
    return;
  }

  const fromAiReferrer = parseAiAssistantReferrerAttribution(referrer, location.pathname);
  if (fromAiReferrer) {
    saveAcquisitionAttributionFirstTouch(fromAiReferrer);
  }
};

/**
 * Loads persisted attribution, or null if none / unavailable.
 * Accepts legacy flat session JSON or `{ capturedAtIso, attribution }` (PBI #1500).
 */
export const loadAcquisitionAttribution = (): AcquisitionAttribution | null => {
  const envelope = readSessionFirstTouchEnvelope();
  return envelope?.attribution ?? null;
};

export const loadSignupUtmSnapshot = (): SignupUtmSnapshot | null => {
  try {
    const raw = sessionStorage.getItem(ACQUISITION_SIGNUP_UTM_SNAPSHOT_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as SignupUtmSnapshot;
  } catch {
    return null;
  }
};

const writeSignupUtmSnapshotFromAttribution = (attribution: AcquisitionAttribution): void => {
  const { utmSource, utmMedium, utmCampaign } = attribution;
  if (!utmSource && !utmMedium && !utmCampaign) {
    return;
  }
  try {
    sessionStorage.setItem(
      ACQUISITION_SIGNUP_UTM_SNAPSHOT_KEY,
      JSON.stringify({ utmSource, utmMedium, utmCampaign }),
    );
  } catch {
    console.warn('Failed to save signup UTM snapshot');
  }
};

export const clearSignupUtmSnapshot = (): void => {
  try {
    sessionStorage.removeItem(ACQUISITION_SIGNUP_UTM_SNAPSHOT_KEY);
  } catch {
    // sessionStorage unavailable
  }
};

/**
 * Removes persisted attribution after successful API persist or discard.
 * Clears session first-touch, last-touch, and durable localStorage (PBI #1500).
 * Does not remove the signup UTM snapshot — see {@link clearSignupUtmSnapshot}.
 */
export const clearAcquisitionAttribution = (): void => {
  try {
    sessionStorage.removeItem(ACQUISITION_ATTRIBUTION_STORAGE_KEY);
    sessionStorage.removeItem(ACQUISITION_ATTRIBUTION_LAST_TOUCH_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable
  }
  deleteDurableAcquisition();
};

/**
 * POSTs sessionStorage attribution to the API (null-only server write) and clears
 * local storage on success. Best-effort — failures leave the payload for a retry.
 * On success, writes signup UTM snapshot before clear when UTMs are present (#1500).
 */
export const persistAcquisitionAttributionIfPresent = async (
  post: (path: string, body: Record<string, string | undefined>) => Promise<unknown>
): Promise<boolean> => {
  const attribution = loadAcquisitionAttribution();
  const lastTouch = loadAcquisitionAttributionLastTouch();
  if (!attribution && !lastTouch) {
    return false;
  }

  // API expects PascalCase JSON via default System.Text.Json case-insensitive;
  // send camelCase to match SPA conventions (ASP.NET Core binds either).
  // First-touch fields come from the first-touch payload; last-touch Google click
  // ids are sent separately for conversion upload (PBI #1204).
  await post('/account/acquisition', {
    gclid: attribution?.gclid,
    gbraid: attribution?.gbraid,
    wbraid: attribution?.wbraid,
    msclkid: attribution?.msclkid,
    fbclid: attribution?.fbclid,
    oppref: attribution?.oppref,
    utmSource: attribution?.utmSource,
    utmMedium: attribution?.utmMedium,
    utmCampaign: attribution?.utmCampaign,
    utmContent: attribution?.utmContent,
    utmTerm: attribution?.utmTerm,
    landingPath: attribution?.landingPath,
    lastTouchGclid: lastTouch?.gclid,
    lastTouchGbraid: lastTouch?.gbraid,
    lastTouchWbraid: lastTouch?.wbraid,
  });

  if (attribution) {
    writeSignupUtmSnapshotFromAttribution(attribution);
  }
  clearAcquisitionAttribution();
  return true;
};

/**
 * UTMs for free_signup: signup snapshot after successful flush, else session fallback.
 */
export const resolveSignupUtmPayload = (): Pick<
  SignupUtmSnapshot,
  'utmSource' | 'utmMedium' | 'utmCampaign'
> => {
  const snapshot = loadSignupUtmSnapshot();
  if (snapshot?.utmSource || snapshot?.utmMedium || snapshot?.utmCampaign) {
    return {
      utmSource: snapshot.utmSource,
      utmMedium: snapshot.utmMedium,
      utmCampaign: snapshot.utmCampaign,
    };
  }
  const session = loadAcquisitionAttribution();
  return {
    utmSource: session?.utmSource,
    utmMedium: session?.utmMedium,
    utmCampaign: session?.utmCampaign,
  };
};
